import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

import { color } from "@/lib/theme";

/* -------------------------------------------------------------- */
/*  Meira assurance seal                                           */
/* -------------------------------------------------------------- */
function Seal({ className = "", valid = true }) {
  const gid = valid ? "seal-g" : "seal-a";
  return (
    <svg width="64" height="64" viewBox="0 0 72 72" fill="none" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
          {valid ? (
            <><stop offset="0" stopColor={color("gold")} /><stop offset="1" stopColor={color("gold-bright")} /></>
          ) : (
            <><stop offset="0" stopColor={color("warn")} /><stop offset="1" stopColor={color("warn")} /></>
          )}
        </linearGradient>
      </defs>
      <circle cx="36" cy="36" r="30" stroke={`url(#${gid})`} strokeWidth="1.5" />
      <circle cx="36" cy="36" r="23" stroke={`url(#${gid})`} strokeWidth="0.75" strokeOpacity="0.6" />
      {Array.from({ length: 48 }).map((_, i) => {
        const a = (i / 48) * Math.PI * 2;
        return <line key={i} x1={36 + Math.cos(a) * 30} y1={36 + Math.sin(a) * 30} x2={36 + Math.cos(a) * 27} y2={36 + Math.sin(a) * 27} stroke={`url(#${gid})`} strokeWidth="0.6" strokeOpacity="0.5" />;
      })}
      {valid ? (
        <path d="M27 37l6 6 12-13" stroke={`url(#${gid})`} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <><path d="M36 26v13" stroke={`url(#${gid})`} strokeWidth="2.6" strokeLinecap="round" /><circle cx="36" cy="46" r="1.6" fill={color("warn")} /></>
      )}
    </svg>
  );
}

/* -------------------------------------------------------------- */
/*  Decorative verification QR (not scannable — ID entry is the   */
/*  real mechanism). Deterministic pattern from the record hash.  */
/* -------------------------------------------------------------- */
function VerifyQR({ seed = "meira", size = 21 }) {
  const bits = [];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  for (let i = 0; i < size * size; i++) {
    h = (h * 1103515245 + 12345) >>> 0;
    bits.push((h >> 16) & 1);
  }
  const finder = (r, c) => (r < 7 && c < 7) || (r < 7 && c >= size - 7) || (r >= size - 7 && c < 7);
  const inFinderModule = (r, c) => {
    const fr = r < 7 ? r : r - (size - 7);
    const fc = c < 7 ? c : c - (size - 7);
    const edge = fr === 0 || fr === 6 || fc === 0 || fc === 6;
    const core = fr >= 2 && fr <= 4 && fc >= 2 && fc <= 4;
    return edge || core;
  };
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="h-full w-full" aria-hidden="true" shapeRendering="crispEdges">
      {Array.from({ length: size }).map((_, r) =>
        Array.from({ length: size }).map((__, c) => {
          const on = finder(r, c) ? inFinderModule(r, c) : bits[r * size + c] === 1;
          return on ? <rect key={`${r}-${c}`} x={c} y={r} width="1" height="1" fill={color("qr-dark")} /> : null;
        })
      )}
    </svg>
  );
}

/* -------------------------------------------------------------- */
/*  A certificate is a document: the composition is the thing that */
/*  is recognised, so it is laid out once at a fixed width and     */
/*  scaled to whatever column it lands in. Phone and desktop get   */
/*  the same artifact at two sizes — never a reflowed one.         */
/* -------------------------------------------------------------- */
const DESIGN_WIDTH = 500;
const MAX_SCALE = 1.28;

function FitToColumn({ children }) {
  const hostRef = useRef(null);
  const cardRef = useRef(null);
  const [fit, setFit] = useState({ scale: 1, height: undefined, offset: 0 });

  const measure = useCallback(() => {
    const host = hostRef.current;
    const card = cardRef.current;
    if (!host || !card) return;
    const available = host.clientWidth;
    if (!available) return;
    // clientWidth / offsetHeight are layout boxes, so an ancestor's transform
    // (the tilt on the home page, reveal animations) cannot feed back in here.
    const scale = Math.min(MAX_SCALE, available / DESIGN_WIDTH);
    const next = {
      scale,
      height: card.offsetHeight * scale,
      offset: Math.max(0, (available - DESIGN_WIDTH * scale) / 2),
    };
    setFit((prev) =>
      Math.abs(prev.scale - next.scale) < 0.0005 &&
      Math.abs((prev.height ?? 0) - next.height) < 0.5 &&
      Math.abs(prev.offset - next.offset) < 0.5
        ? prev
        : next
    );
  }, []);

  useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(hostRef.current);
    ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <div ref={hostRef} className="w-full" style={{ height: fit.height }}>
      <div
        ref={cardRef}
        style={{
          width: DESIGN_WIDTH,
          marginLeft: fit.offset,
          transform: `scale(${fit.scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// Tokens, not colours: a coverage result is a judgement, so it takes the
// status palette and follows it wherever that moves.
const COV_STYLE = {
  pass: { token: "valid", label: "Pass" },
  review: { token: "warn", label: "Review" },
  fail: { token: "danger", label: "Fail" },
};

function Section({ title, children }) {
  return (
    <div className="border-t border-line pt-5">
      <div className="mb-3 font-mono text-[9px] uppercase tracking-[0.22em] text-content-faint">{title}</div>
      {children}
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="rounded-full border border-line bg-panel-raised px-2.5 py-1 font-mono text-[10px] tracking-wide text-content-muted">
      {children}
    </span>
  );
}

/**
 * Canonical Meira verifiable assurance certificate — premium native web artifact.
 * Renders richly when full data present, degrades gracefully otherwise.
 */
export default function Certificate({ cert, className = "", testId = "assurance-certificate" }) {
  const c = cert || {};
  const isValid = c.status !== "expired";
  const fs = c.findings_summary;

  const meta = [
    ["Certificate ID", c.certificate_id],
    ["Organization", c.organization],
    ["AI System", c.ai_system],
    ["System Type", c.system_type],
    ["Assessment Type", c.assessment_type],
    ["Assurance Level", c.assurance_level],
    ["Issued", c.issued_date],
    ["Valid Until", c.valid_until],
  ].filter(([, v]) => v);

  return (
    <FitToColumn>
      <div
        data-testid={testId}
        className={`relative overflow-hidden rounded-md border bg-panel p-6 ${isValid ? "border-gold/40 shadow-lift-gold" : "border-warn/30 shadow-panel"} ${className}`}
      >
        {/* undertone + watermark */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand/[0.06] via-transparent to-gold/[0.04]" />
        <div className={`pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full blur-3xl ${isValid ? "bg-gold/[0.05]" : "bg-warn/[0.05]"}`} />
        <span aria-hidden className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none font-display text-[8rem] font-medium leading-none text-content/[0.02]">MEIRA</span>

        <div className={`relative rounded-sm border border-dashed p-5 ${isValid ? "border-gold/25" : "border-warn/25"}`}>
          {/* header */}
          <div className="flex items-start justify-between gap-4 border-b border-line pb-5">
            <div className="min-w-0 flex-1">
              <div className="font-mono text-[9px] uppercase tracking-[0.26em] text-gold">Meira · Verifiable AI Assurance</div>
              <h4 className="mt-2 font-display text-[22px] font-medium leading-tight text-content">Certificate of AI Assurance</h4>
              <p className="mt-2 text-[11px] leading-relaxed text-content-muted">
                The named AI system was tested and verified against the defined scope of this Meira assurance assessment.
              </p>
              {c.sample && (
                <div className="mt-3 inline-flex items-center rounded-full border border-line bg-panel-raised px-2.5 py-1 font-mono text-[8.5px] uppercase tracking-[0.16em] text-content-faint">
                  Sample record · Demonstration data
                </div>
              )}
            </div>
            <Seal valid={isValid} className={`${isValid ? "gold-glow" : ""} h-16 w-16 shrink-0`} />
          </div>

          {/* meta grid */}
          <div className="grid grid-cols-2 gap-x-7 gap-y-3.5 pt-5">
            {meta.map(([k, v]) => (
              <div key={k} className="flex min-w-0 flex-col gap-1">
                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">{k}</span>
                <span className={`text-[13px] leading-snug ${k === "Organization" || k === "AI System" ? "font-display text-gold-bright" : k === "Certificate ID" ? "font-mono text-gold" : "text-content"}`}>{v}</span>
              </div>
            ))}
          </div>

          {/* testing engines */}
          {c.testing_engines?.length ? (
            <div className="pt-6"><Section title="Testing engines"><div className="flex flex-wrap gap-2">{c.testing_engines.map((e) => <Chip key={e}>{e}</Chip>)}</div></Section></div>
          ) : null}

          {/* standards screened */}
          {c.frameworks?.length ? (
            <div className="pt-5"><Section title="Standards screened"><div className="flex flex-wrap gap-2">{c.frameworks.map((f) => <Chip key={f}>{f}</Chip>)}</div></Section></div>
          ) : null}

          {/* test coverage */}
          {c.test_coverage?.length ? (
            <div className="pt-5"><Section title="Test coverage">
              <ul className="flex flex-col gap-2">
                {c.test_coverage.map((t) => {
                  const s = COV_STYLE[t.result] || COV_STYLE.pass;
                  return (
                    <li key={t.category} className="flex items-center justify-between gap-3">
                      <span className="text-[13px] text-content">{t.category}</span>
                      <span className="inline-flex shrink-0 items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: color(s.token) }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: color(s.token) }} />{s.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Section></div>
          ) : null}

          {/* findings summary */}
          {fs ? (
            <div className="pt-5"><Section title="Findings summary">
              <div className="grid grid-cols-4 gap-2">
                {[["Critical", fs.critical, "danger"], ["High", fs.high, "warn"], ["Medium", fs.medium, "brand"], ["Resolved", fs.resolved, "valid"]].map(([label, n, token]) => (
                  <div key={label} className="rounded-md border border-line bg-panel-raised/60 p-2.5 text-center">
                    <div className="font-display text-2xl font-light" style={{ color: color(token) }}>{n ?? 0}</div>
                    <div className="mt-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-content-faint">{label}</div>
                  </div>
                ))}
              </div>
            </Section></div>
          ) : null}

          {/* score + verified */}
          <div className="mt-6 flex items-end justify-between gap-4 border-t border-line pt-5">
            <div>
              <div className="flex items-end gap-2">
                <span className="text-gradient-gold font-display text-5xl font-light leading-none">{c.trust_score ?? "—"}</span>
                <span className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-content-faint">/ 100</span>
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-content-muted">Meira Trust Score</span>
                <span className="rounded-full border border-line px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.14em] text-content-faint">Illustrative</span>
              </div>
            </div>
            <div className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.16em] ${isValid ? "border-valid/30 bg-valid/10 text-valid shadow-none" : "border-warn/30 bg-warn/10 text-warn"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${isValid ? "bg-valid" : "bg-warn"}`} />
              {isValid ? "Verified" : "Not current"}
            </div>
          </div>

          {/* QR + public verification */}
          <div className="mt-6 flex items-center gap-4 border-t border-line pt-5">
            <div className="h-[76px] w-[76px] shrink-0 rounded-md border border-gold/25 bg-qr-light p-2">
              <VerifyQR seed={c.verification_hash || c.certificate_id || "meira"} />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-content-faint">Public verification record</div>
              <p className="mt-1.5 text-[11px] leading-relaxed text-content-muted">
                Check authenticity and current status by entering the certificate ID on the public verification page — no login required.
              </p>
              {c.verification_hash && (
                <div className="mt-1.5 truncate font-mono text-[9px] tracking-wide text-content-faint">Record · {c.verification_hash.slice(0, 20)}…</div>
              )}
            </div>
          </div>
        </div>

        <p className="relative mt-4 text-center font-mono text-[9px] leading-relaxed tracking-wide text-content-faint">
          Not an ISO/IEC 42001 certificate. Verified against the defined scope and criteria of the recorded Meira assurance assessment — not a guarantee of universal or permanent safety.
        </p>
      </div>
    </FitToColumn>
  );
}
